'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { motion, AnimatePresence } from 'motion/react';

import { SmartLink } from '../../fragments/smart-link/SmartLink';
import Button from '../../fragments/button/Button';
import Cta from '../../fragments/cta/Cta';
import IconUI from '../../fragments/icon/IconUI';
import NavigationMainLogo from '../navigation-desktop/NavigationMainLogo';
import { useIsDesktop } from '../../context/ThemeContextManagement';

// Slide direction for the level transitions: forward (drill in) / back. The
// outgoing level slides fully out one side while the incoming level slides in
// from the other, so they cross over (handled by AnimatePresence popLayout).
const levelVariants = {
  enter: (dir) => ({ x: dir > 0 ? '100%' : '-100%' }),
  center: { x: '0%' },
  exit: (dir) => ({ x: dir > 0 ? '-100%' : '100%' }),
};

// Mobile navigation — a drill-down menu (Level 1 → 2 → 3) built from the same
// `block.menuItems` tree as the desktop nav. Shown below the desktop breakpoint.
// The search overlay is owned by NavigationMain; `onOpenSearch` triggers it.
export default function NavigationMobile({ block, onOpenSearch }) {
  const router = useRouter();
  const isDesktop = useIsDesktop();

  const [open, setOpen] = useState(false);
  const [stack, setStack] = useState([]); // items we've drilled into
  const [direction, setDirection] = useState(1);

  const topLevel = block?.menuItems ?? [];
  const applyLink = block?.applyLink;

  const currentParent = stack[stack.length - 1] || null;
  const parentOverviewLink =
    currentParent && currentParent.uri && currentParent.uri !== '/'
      ? { id: '__parent__', uri: currentParent.uri, label: 'Overview' }
      : null;
  const currentItems = currentParent
    ? parentOverviewLink
      ? [parentOverviewLink, ...(currentParent.children || [])]
      : currentParent.children || []
    : topLevel;
  const isRoot = stack.length === 0;

  // Close + reset whenever the route changes.
  useEffect(() => {
    const handleRouteChange = () => setOpen(false);
    router.events.on('routeChangeStart', handleRouteChange);
    // Same-page clicks cancel navigation (no routeChangeStart), so SmartLink
    // emits `samePageNav` — close the menu on that too.
    window.addEventListener('samePageNav', handleRouteChange);
    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
      window.removeEventListener('samePageNav', handleRouteChange);
    };
  }, [router]);

  // Close if the viewport is resized past the desktop breakpoint while open.
  useEffect(() => {
    if (isDesktop) setOpen(false);
  }, [isDesktop]);

  // Lock background scroll while the menu is open; reset the stack when closed.
  useEffect(() => {
    if (open) {
      window.lenis?.stop();
    } else {
      window.lenis?.start();
      setStack([]);
      setDirection(1);
    }
    return () => window.lenis?.start();
  }, [open]);

  // Guard sits below every hook so the hook order is identical on every render.
  if (!block || !block.menuItems) return null;

  const drillInto = (item) => {
    setDirection(1);
    setStack((prev) => [...prev, item]);
  };

  const goBack = () => {
    setDirection(-1);
    setStack((prev) => prev.slice(0, -1));
  };

  const closeMenu = () => setOpen(false);

  const renderItem = (item) => {
    const hasChildren = item.children?.length > 0;

    const inner = (
      <span className="NavigationMobile-itemText">
        <span className="NavigationMobile-itemLabel headingStrong">
          {item.label}
        </span>
        {item.excerpt && (
          <span className="NavigationMobile-itemDesc">{item.excerpt}</span>
        )}
      </span>
    );

    return (
      <li className="NavigationMobile-item" key={item.id}>
        {hasChildren ? (
          <button
            type="button"
            className="NavigationMobile-itemLink"
            onClick={() => drillInto(item)}
          >
            {inner}
            <IconUI icon="chevronRight" />
          </button>
        ) : (
          <SmartLink
            href={item.uri}
            className="NavigationMobile-itemLink"
            onClick={closeMenu}
          >
            {inner}
            <IconUI icon="chevronRight" />
          </SmartLink>
        )}
      </li>
    );
  };

  return (
    <nav
      className={`NavigationMobile ${open ? 'is-open' : ''}`}
      aria-label="Main Navigation"
    >
      <div className="NavigationMobile-bar">
        <Button
          className="NavigationMobile-search"
          icon="search"
          theme="tertiary"
          aria-label="Open search"
          onClick={onOpenSearch}
        />

        <SmartLink
          href={block.site.link}
          className="NavigationMobile-logo"
          title={block.site.title}
        >
          <NavigationMainLogo />
        </SmartLink>

        <Button
          className="NavigationMobile-toggle"
          theme="tertiary"
          icon={open ? 'close' : 'menu'}
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          onClick={() => setOpen((prev) => !prev)}
        />
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            className="NavigationMobile-panel"
            // initial={{ opacity: 0, clipPath: 'inset(0 0 100% 0)' }}
            initial={{ clipPath: 'inset(0 0 100% 0)' }}
            animate={{
              // opacity: 1,
              clipPath: 'inset(0 0 0% 0)',
              // transition: { delay: 0.135, duration: 0.6 },
              transition: { delay: 0.14, duration: 0.4, ease: 'easeInOut' },
            }}
            exit={{
              clipPath: 'inset(0 0 100% 0)',
              transition: { duration: 0.3, ease: 'easeOut' },
            }}
            // transition={{ duration: 0.25, ease: 'easeInOut' }}
          >
            <div className="NavigationMobile-panelInner">
              <AnimatePresence
                mode="popLayout"
                custom={direction}
                initial={false}
              >
                <motion.div
                  key={currentParent?.id || 'root'}
                  className="NavigationMobile-level"
                  custom={direction}
                  variants={levelVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.35, ease: 'easeInOut' }}
                >
                  {currentParent && (
                    <button
                      type="button"
                      className="NavigationMobile-back"
                      onClick={goBack}
                    >
                      <IconUI icon="chevronLeft" />
                      <span className="NavigationMobile-itemLabel headingStrong">
                        {currentParent.label}
                      </span>
                    </button>
                  )}

                  <ul
                    className={`NavigationMobile-list ${
                      isRoot ? 'NavigationMobile-list--root' : ''
                    }`}
                  >
                    {currentItems.map(renderItem)}
                  </ul>

                  {isRoot && (
                    <div className="NavigationMobile-footer">
                      {block.contactLinks?.length > 0 && (
                        <ul className="NavigationMobile-contact">
                          {block.contactLinks.map((item, index) => (
                            <li
                              key={index}
                              className="NavigationMobile-contactRow"
                            >
                              <a
                                href={item.url || '#'}
                                target={item.target || undefined}
                                className="NavigationMobile-contactLink"
                              >
                                <IconUI
                                  icon={
                                    String(item.url).startsWith('tel:')
                                      ? 'phone'
                                      : 'markerPin'
                                  }
                                />
                                <span className="step--1">{item.title}</span>
                              </a>
                            </li>
                          ))}
                        </ul>
                      )}

                      {applyLink && (
                        <Cta
                          size="large"
                          // icon="arrowNarrowRight"
                          cta={{
                            url: applyLink.url,
                            buttonLabel: applyLink.title,
                            isExternal: !!applyLink.target,
                          }}
                        />
                      )}
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
